<template>
  <div class="aside">
    <div class="aside-main">
      <div class="aside-block">
        <router-link
          class="aside-section-link body bold"
          :to="{ name: 'Dashboard' }"
        >
          <div class="link-icon"><DashboardIcon /></div>

          <span>{{ $t('aside.dashboard') }}</span>
        </router-link>
      </div>
      <div class="aside-block">
        <router-link
          class="aside-section-link body bold"
          :to="{ name: 'ScoreDetails' }"
        >
          <div class="link-icon"><ScoreIcon /></div>

          <span>{{ $t('aside.score_details') }}</span>
        </router-link>
      </div>
      <div class="aside-block">
        <router-link
          class="aside-section-link body bold"
          :to="{ name: 'KPIDetails' }"
        >
          <div class="link-icon"><KPIDetailIcon /></div>

          <span>{{ $t('aside.kpi_details') }}</span>
        </router-link>
      </div>
      <div v-if="isAdmin || isLead" class="aside-block">
        <router-link
          class="aside-section-link body bold"
          :to="{ name: 'Targets' }"
        >
          <div class="link-icon"><TargetsIcon /></div>

          <span>{{ $t('aside.targets') }}</span>
        </router-link>
      </div>
      <div v-if="!isOfficer" class="aside-block">
        <router-link
          class="aside-section-link body bold"
          :to="{ name: 'ScoreCard' }"
        >
          <div class="link-icon">
            <ScoreCardIcon />
          </div>

          <span>{{ $t('aside.score_card') }}</span>
        </router-link>
      </div>
    </div>
    <div class="aside-bottom">
      <div v-if="isAdmin || isLead" class="aside-bottom_management">
        <div class="aside-block">
          <p class="management-title">{{ $t('aside.management') }}</p>
          <router-link
            class="aside-section-link body bold"
            :to="{ name: 'UserManagement' }"
          >
            <div class="link-icon"><UsersIcon /></div>

            <span>{{ $t('aside.users') }}</span>
          </router-link>
        </div>
        <div class="aside-block">
          <router-link
            class="aside-section-link body bold"
            :to="{ name: 'KPI' }"
          >
            <div class="link-icon"><KPIIcon /></div>

            <span>{{ $t('aside.kpi') }}</span>
          </router-link>
        </div>
      </div>
      <div v-if="storeUser.user" class="aside-bottom_user">
        <div class="profile">
          <UserIcon />
          <div class="profile_text">
            <div class="profile_name">
              {{ storeUser.user.first_name }} {{ storeUser.user.last_name }}
            </div>
            <div
              v-if="storeUser.user.roles && storeUser.user.roles.length"
              class="profile_role"
            >
              {{ storeUser.user.roles[0].user_role_type_desc }}
            </div>
          </div>
        </div>
        <button type="button" class="btn-link" @click="showModal = true">
          {{ $t('aside.logout.btn_logout') }}
        </button>
      </div>
    </div>

    <AppModal v-model="showModal" :title="$t('aside.logout.modal.title')">
      <template #body
      ><h3 class="logout-modal_text text-center my-0">
        {{ $t('aside.logout.modal.text') }}
      </h3></template
      >
      <template #footer>
        <app-button
          btn-type="secondary mx-0 my-0 me-4"
          @click="showModal = false"
        >{{ $t('aside.logout.modal.btn_no') }}
        </app-button>
        <app-button btn-type="primary mx-0 ms-2 my-0" @click="logOut"
        >{{ $t('aside.logout.modal.btn_yes') }}
        </app-button>
      </template>
    </AppModal>
  </div>
</template>

<script setup>
import AppButton from '@/components/AppButton.vue'
import AppModal from '@app/components/UI/AppModal.vue'
import DashboardIcon from '@app/components/icons/DashboardIcon.vue'
import ScoreIcon from '@app/components/icons/ScoreIcon.vue'
import KPIDetailIcon from '@app/components/icons/KPIDetailIcon.vue'
import TargetsIcon from '@app/components/icons/TargetsIcon.vue'
import UsersIcon from '@app/components/icons/UsersIcon.vue'
import UserIcon from '@app/components/icons/UserIcon.vue'
import KPIIcon from '@app/components/icons/KPIIcon.vue'
import ScoreCardIcon from '@app/components/icons/ScoreCardIcon.vue'
import { useRouter } from 'vue-router'
import { computed, ref } from 'vue'
import { useUserStore } from '@app/store/user'

const storeUser = useUserStore()
const router = useRouter()

const userStore = useUserStore()
const isAdmin = computed(() => userStore.isAdministrator)
const isLead = computed(() => userStore.isLead)
const isOfficer = computed(() => userStore.isOfficer)

const showModal = ref(false)

/**
 * Asynchronous function to log out the user.
 * @returns {Promise<void>} A Promise that resolves when the logout process is complete.
 */
const logOut = async () => {
  await storeUser.logOut()
  showModal.value = false
  router.push('/sign_in')
}
</script>

<style lang="scss" scoped>
.aside {
  @include md {
    display: none;
  }

  position: sticky;
  top: 86px;

  display: flex;
  flex-direction: column;
  align-self: flex-start;
  justify-content: space-between;

  width: 336px;
  min-width: 336px;
  height: calc(100vh - 110px);
  margin: 0 12px;
  padding: 24px 24px 0;

  background: var(--app-color-white);
  border-radius: 4px;

  &-block {
    width: 100%;
    margin-bottom: 4px;
  }

  &-section-link {
    display: flex;
    align-items: center;

    width: 100%;
    padding: 10px;

    font-size: 16px;
    color: var(--app-color-black);
    text-decoration: none;

    border-radius: 4px;
    box-shadow: none;

    .link-icon {
      display: flex;
      align-items: center;
      justify-content: center;

      width: 24px;
      height: 24px;
      margin-right: 10px;

      transition: all 0.2s;
    }

    &.active {
      color: var(--app-color-primary);
      background-color: var(--app-color-blue-100);
    }

    &:hover,
    &:active {
      color: var(--app-color-primary);
    }

    & + .aside-link {
      margin-top: 4px;
    }
  }

  &-link {
    display: flex;
    align-items: center;

    height: 19px;
    margin-left: 44px;

    font-size: 16px;
    color: var(--app-color-black);
    text-decoration: none;

    background: none;
    box-shadow: none;

    &.router-link-exact-active {
      font-weight: 500;
      color: var(--app-color-primary);
    }

    &:hover,
    &:active {
      color: var(--app-color-primary);
    }

    & + .aside-link {
      margin-top: 16px;
    }
  }

  &-bottom {
    &_management {
      padding-bottom: 40px;

      .management-title {
        margin-bottom: 8px;
        padding-left: 14px;
        font-weight: 500;
        color: #6d6e70;
      }
    }

    &_user {
      display: flex;
      align-items: center;
      justify-content: space-between;

      margin: 0 -24px;
      padding: 18px 24px;

      color: #000;
      text-decoration: none;

      border-top: 1px solid var(--app-color-gray-200);

      .profile {
        display: flex;
        align-items: center;

        &_text {
          margin-left: 10px;
        }

        &_name {
          font-size: 18px;
          font-weight: 500;
          line-height: 21px;
          color: var(--app-color-black);
        }

        &_role {
          font-size: 18px;
          line-height: 21px;
          color: #808183;
        }
      }

      .btn-link {
        min-width: max-content;
        margin: 0;
        padding: 0;

        color: var(--app-color-primary);
        text-decoration: none;

        background: none;
        border: none;
        outline: none;
      }
    }
  }
}
</style>
